import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { Alert, AlertPriorityType, DetectionEngine } from "@/constants/mockData";
import { getApiUrl, fetchWithAuth, getWebSocketUrl } from "@/lib/query-client";
import { subscribeMarketSocket } from "@/lib/marketSocket";
import { publicMarketLabel } from "@/lib/publicTerminology";

// ── Alert suppression (2026-07-08 broken-promise fix) ─────────────────────────
// Dismiss used to filter only the in-memory wsAlerts array and snooze only
// marked-as-read: both alerts resurrected on the next REST refetch, WS push,
// or app restart. Suppression is now persisted and applied at the merge
// point, so EVERY consumer of this hook respects it.
const DISMISSED_KEY = "@fahali_dismissed_alerts"; // { [alertId]: dismissedAtMs }
const SNOOZED_KEY = "@fahali_snoozed_alerts"; // { [alertId]: snoozedUntilMs }
const DISMISSED_TTL_MS = 14 * 24 * 3600_000; // matches server alert retention
export const DEFAULT_SNOOZE_MS = 4 * 3600_000; // 4h — one judgment horizon

type SuppressionMap = Record<string, number>;

async function loadSuppression(key: string): Promise<SuppressionMap> {
  try {
    const raw = await AsyncStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function pruneSuppression(map: SuppressionMap, isExpired: (ts: number) => boolean): SuppressionMap {
  const out: SuppressionMap = {};
  for (const [id, ts] of Object.entries(map)) {
    if (!isExpired(ts)) out[id] = ts;
  }
  return out;
}

type ConnectionStatus = "connected" | "connecting" | "disconnected";

/**
 * Whether the alert list is KNOWN. An empty list is only an all-clear when
 * status is "ready"; before that it is "not read yet", and on "error" it is
 * "could not read". The Alerts screen used to say "no active alert is asking
 * for your attention" in all three.
 */
export type AlertFeedStatus = "idle" | "loading" | "ready" | "error";

export interface IntelToast {
  id: string;
  type: "institutional_signal" | "smart_money";
  title: string;
  message: string;
  timestamp: Date;
}

interface UseRealTimeAlertsReturn {
  alerts: Alert[];
  /** Alerts on the reader's own holdings (the ones that interrupt a phone). */
  heldAlerts: Alert[];
  /** Symbols the held list covers; 0 means no book, not "nothing happened". */
  heldSymbolCount: number | null;
  status: AlertFeedStatus;
  heldStatus: AlertFeedStatus;
  intelToasts: IntelToast[];
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  newAlertCount: number;
  clearNewAlerts: () => void;
  dismissAlert: (id: string) => void;
  snoozeAlert: (id: string, durationMs?: number) => void;
  markAsRead: (id: string) => void;
  refetch: () => void;
  clearIntelToasts: () => void;
}

function mapAlertTypeToPriority(alertType: string | null): AlertPriorityType {
  if (!alertType) return "moderate";
  const t = alertType.toLowerCase();
  if (t.includes("flash_crash") || t.includes("liquidation") || t.includes("tail_dependence")) return "critical";
  if (t.includes("dark_pool") || t.includes("whale") || t.includes("crash")) return "high";
  if (t.includes("funding") || t.includes("risk")) return "moderate";
  return "low";
}

function mapAlertTypeToEngine(alertType: string | null): DetectionEngine {
  if (!alertType) return "statistical";
  const t = alertType.toLowerCase();
  if (t.includes("dark_pool")) return "darkPool";
  if (t.includes("flash_crash")) return "flash_crash";
  if (t.includes("tail_dependence")) return "tail_dependence";
  if (t.includes("funding")) return "funding_stress";
  if (t.includes("liquidation")) return "liquidation";
  if (t.includes("whale")) return "orderBook";
  if (t.includes("news") || t.includes("sentiment")) return "news_sentiment";
  if (t.includes("volatility")) return "volatility";
  if (t.includes("multi") || t.includes("confluence")) return "multi_indicator";
  return "statistical";
}

function safeStr(val: any, fallback: string): string {
  if (val === null || val === undefined) return fallback;
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return fallback;
}

/**
 * Seconds until the detection's registered claim horizon, or null if it has none.
 *
 * Every moat row is registered with its horizon BEFORE the outcome exists, so when
 * a horizon is present this is a real measurement, not an estimate. When it is
 * absent we return null and the UI shows "—".
 *
 * Accepts the shapes the backend actually emits: `horizon_min` / `horizonMin`
 * (minutes), `horizon_seconds`, or `horizon` as either a number of minutes or a
 * string like "60min" / "4h" / "24h".
 */
function horizonSeconds(det: Record<string, any>, meta: Record<string, any>): number | null {
  const src = { ...meta, ...det };

  const secs = Number(src.horizon_seconds ?? src.horizonSeconds);
  if (Number.isFinite(secs) && secs > 0) return Math.round(secs);

  const mins = Number(src.horizon_min ?? src.horizonMin ?? src.horizon_minutes);
  if (Number.isFinite(mins) && mins > 0) return Math.round(mins * 60);

  const raw = src.horizon;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return Math.round(raw * 60);
  if (typeof raw === "string") {
    // "90min" | "4h" | "48 h" | "3600s" — unit decides the multiplier, and an
    // unrecognised unit returns null rather than guessing minutes.
    const m = raw.trim().match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|seconds|m|min|mins|minutes|h|hr|hrs|hours|d|day|days)?$/i);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > 0) {
        const unit = (m[2] || "min").toLowerCase();
        if (/^(s|sec|secs|seconds)$/.test(unit)) return Math.round(n);
        if (/^(m|min|mins|minutes)$/.test(unit)) return Math.round(n * 60);
        if (/^(h|hr|hrs|hours)$/.test(unit)) return Math.round(n * 3600);
        if (/^(d|day|days)$/.test(unit)) return Math.round(n * 86400);
      }
    }
  }
  return null;
}

function mapDbRowToAlert(row: any): Alert {
  const conf = typeof row.confidence === "string" ? parseFloat(row.confidence) : (row.confidence ?? 0);
  const normalized = conf > 1 ? conf / 100 : conf;
  const alertType = row.signalType || row.signal_type || row.alert_type || null;
  const symbolStr = safeStr(row.symbol, "MARKET_WIDE");
  const reasoningStr = safeStr(row.reasoning, "");
  const meta = row.metadata || {};
  const det = row.details || {};

  return {
    id: String(row.id),
    priority: mapAlertTypeToPriority(alertType),
    title: alertType ? publicMarketLabel(alertType) : "Unusual market activity",
    asset: symbolStr,
    // Null, not 0 — see Alert.price. This mapper had no price-change source
    // at all, so every card it built rendered "$0.00  +0.00%" in green.
    price: row.price_target ? parseFloat(row.price_target) : null,
    priceChange: null,
    description: reasoningStr
      || `${publicMarketLabel(alertType, "Unusual market activity")} on ${symbolStr}`,
    // Real registered horizon when the detection carries one, else null ("—").
    // Never 0 — formatTimeToImpact renders <=0 as "Now", i.e. maximum urgency.
    timeToImpact: horizonSeconds(det, meta),
    confidence: normalized,
    recommendations: reasoningStr ? [reasoningStr] : [],
    timestamp: row.timestamp ? new Date(row.timestamp) : new Date(),
    engine: mapAlertTypeToEngine(alertType),
    isRead: false,
    alertType: alertType || undefined,
    symbol: symbolStr,
    message: row.message || undefined,
    severity: row.severity || undefined,
    details: det,
    metadata: meta,
    observation: meta.observation || det.observation || undefined,
    causality: meta.causality || det.causality || undefined,
    actionPlan: meta.actionPlan || det.actionPlan || undefined,
    actionType: meta.actionType || det.actionType || undefined,
    validationChain: meta.validationChain || det.validationChain || undefined,
    narrative: meta.narrative || det.narrative || undefined,
  };
}

function mapApiAlert(row: any): Alert {
  return {
    id: String(row.id),
    priority: row.priority || "moderate",
    title: row.title || publicMarketLabel(row.alertType || row.alert_type, "Unusual market activity"),
    asset: row.asset || row.symbol || "MARKET_WIDE",
    // Preserve null rather than coercing to 0 — see Alert.price. The server
    // stopped emitting `|| 0` on 2026-08-27; re-manufacturing the zero here
    // would have made that fix invisible in the UI.
    price: row.price == null ? null : Number(row.price),
    priceChange: row.priceChange == null ? null : Number(row.priceChange),
    description: row.description || row.message || "",
    // Preserve null rather than coercing to 0 — see Alert.timeToImpact.
    timeToImpact: row.timeToImpact == null ? null : Number(row.timeToImpact),
    confidence: Number(row.confidence ?? 0),
    recommendations: Array.isArray(row.recommendations)
      ? row.recommendations
      : row.description
        ? [row.description]
        : [],
    timestamp: row.timestamp ? new Date(row.timestamp) : new Date(),
    engine: row.engine || "statistical",
    isRead: Boolean(row.isRead ?? row.acknowledged ?? false),
    isHeld: row.isHeld === true ? true : undefined,
    // Preserve raw fields for EngineCard
    alertType: row.alertType || row.alert_type || undefined,
    symbol: row.symbol || row.asset || undefined,
    message: row.message || undefined,
    severity: row.severity || undefined,
    details: row.details || undefined,
    metadata: row.metadata || undefined,
    observation: row.observation || undefined,
    causality: row.causality || undefined,
    actionPlan: row.actionPlan || undefined,
    actionType: row.actionActionType || row.actionType || undefined,
    validationChain: row.validationChain || undefined,
    narrative: row.narrative || undefined,
    onset: row.onset && typeof row.onset === "object"
      ? { state: row.onset.state ?? null, label: typeof row.onset.label === "string" ? row.onset.label : null, reason: row.onset.reason }
      : null,
  };
}

function sortAlerts(alerts: Alert[]): Alert[] {
  const priorityOrder: Record<AlertPriorityType, number> = {
    critical: 0, high: 1, moderate: 2, low: 3
  };
  return [...alerts].sort((a, b) => {
    const pDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (pDiff !== 0) return pDiff;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

export function useRealTimeAlerts(initialAlerts: Alert[] = [], enabled = true): UseRealTimeAlertsReturn {
  const recentQuery = useQuery<Alert[]>({
    queryKey: ["/api/alerts/recent"],
    queryFn: async () => {
      const apiUrl = getApiUrl();
      if (!apiUrl) throw new Error("API URL unavailable");
      const res = await fetchWithAuth(`${apiUrl}/api/alerts/recent`);
      // Throw, never `return []`: an empty array renders as "no alert is
      // asking for your attention", which a failed read cannot establish.
      if (!res.ok) throw new Error(`Recorded alerts unavailable (${res.status})`);
      const data = await res.json();
      const raw = data.alerts || [];
      if (!Array.isArray(raw) || raw.length === 0) return [];
      return sortAlerts(raw.map(mapApiAlert));
    },
    staleTime: 30_000,
    refetchInterval: 300_000, // 5 min fallback polling
    retry: 2,
    enabled,
  });
  const { data: restAlerts, refetch: refetchRest } = recentQuery;

  // The reader's own holdings. /api/alerts/recent is the latest 50 across the
  // whole market, so a pushed alert on a held name had usually scrolled out of
  // it by the time the reader opened the app (2026-09-29, TSLA 50888057).
  const heldQuery = useQuery<{ alerts: Alert[]; heldSymbolCount: number }>({
    queryKey: ["/api/alerts/held"],
    queryFn: async () => {
      const apiUrl = getApiUrl();
      if (!apiUrl) throw new Error("API URL unavailable");
      const res = await fetchWithAuth(`${apiUrl}/api/alerts/held`);
      // 401 = no identity to hold anything: a known "no book", not a failure.
      if (res.status === 401) return { alerts: [], heldSymbolCount: 0 };
      if (!res.ok) throw new Error(`Alerts on your holdings unavailable (${res.status})`);
      const data = await res.json();
      const raw = Array.isArray(data.alerts) ? data.alerts : [];
      return {
        alerts: raw.map((r: any) => ({ ...mapApiAlert(r), isHeld: true })),
        heldSymbolCount: Number.isFinite(Number(data.heldSymbolCount)) ? Number(data.heldSymbolCount) : 0,
      };
    },
    staleTime: 30_000,
    refetchInterval: 120_000,
    retry: 2,
    enabled,
  });

  const [wsAlerts, setWsAlerts] = useState<Alert[]>([]);
  const [intelToasts, setIntelToasts] = useState<IntelToast[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("connecting");
  const [newAlertCount, setNewAlertCount] = useState(0);
  // (No socket refs. This hook borrows the shared socket and owns no
  // connection — `wsRef`/`reconnectTimeoutRef`/`reconnectAttemptsRef` survived
  // the 08-14 consolidation as dead reads and made the file look like it still
  // ran its own reconnect policy, which is exactly the misreading that let the
  // missing `onStatus` above go unnoticed.)

  // Merge REST (cached + shared) with WS append-only updates
  // Persisted suppression — loaded once, applied at the merge point below so
  // dismissed/snoozed alerts stay gone across refetches, pushes and restarts.
  const [dismissed, setDismissed] = useState<SuppressionMap>({});
  const [snoozed, setSnoozed] = useState<SuppressionMap>({});
  useEffect(() => {
    if (!enabled) {
      setConnectionStatus("disconnected");
      return;
    }
    let cancelled = false;
    (async () => {
      const now = Date.now();
      const [d, s] = await Promise.all([loadSuppression(DISMISSED_KEY), loadSuppression(SNOOZED_KEY)]);
      const dPruned = pruneSuppression(d, (ts) => now - ts > DISMISSED_TTL_MS);
      const sPruned = pruneSuppression(s, (until) => until <= now);
      if (cancelled) return;
      setDismissed(dPruned);
      setSnoozed(sPruned);
      AsyncStorage.setItem(DISMISSED_KEY, JSON.stringify(dPruned)).catch(() => {});
      AsyncStorage.setItem(SNOOZED_KEY, JSON.stringify(sPruned)).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  const baseAlerts = restAlerts?.length ? restAlerts : initialAlerts;
  const rawHeld = heldQuery.data?.alerts;
  const heldIds = useMemo(() => new Set((rawHeld ?? []).map((a) => a.id)), [rawHeld]);
  const heldAlerts = useMemo(() => {
    const now = Date.now();
    return (rawHeld ?? []).filter((a) =>
      dismissed[a.id] === undefined && !(snoozed[a.id] !== undefined && snoozed[a.id] > now));
  }, [rawHeld, dismissed, snoozed]);
  const alerts = useMemo(() => {
    const now = Date.now();
    const suppressed = (a: Alert) =>
      dismissed[a.id] !== undefined || (snoozed[a.id] !== undefined && snoozed[a.id] > now);
    // Held alerts join the list even after they scroll out of the market-wide
    // 50, and any copy of a held alert is marked as held.
    const markHeld = (a: Alert) => (heldIds.has(a.id) ? { ...a, isHeld: true } : a);
    const visibleWs = wsAlerts.filter((a) => !suppressed(a)).map(markHeld);
    const visibleBase = baseAlerts.filter((a) => !suppressed(a)).map(markHeld);
    const seen = new Set<string>();
    const merged: Alert[] = [];
    for (const a of [...visibleWs, ...heldAlerts, ...visibleBase]) {
      if (seen.has(a.id)) continue;
      seen.add(a.id);
      merged.push(a);
    }
    return sortAlerts(merged).slice(0, 150);
  }, [baseAlerts, wsAlerts, heldAlerts, heldIds, dismissed, snoozed]);

  // Data in hand = ready, even if a background refetch later failed.
  const statusOf = (q: { data: unknown; isError: boolean }): AlertFeedStatus =>
    !enabled ? "idle" : q.data !== undefined ? "ready" : q.isError ? "error" : "loading";
  const status = statusOf(recentQuery);
  const heldStatus = statusOf(heldQuery);

  // WebSocket — real-time alert updates (degrades gracefully to REST polling)
  useEffect(() => {
    if (!enabled) return;
    // Transport is the SHARED socket (client/lib/marketSocket.ts). This hook
    // used to open its own, as did four others — five connections to the same
    // URL against the server's 6-per-client cap. The message handling below
    // is unchanged; only the socket is now borrowed rather than owned.
    //
    // 🔴 `onStatus` is NOT optional decoration — it is the only writer of
    // `connectionStatus`. The 2026-08-14 consolidation moved four hooks onto
    // this shared socket and carried `onMessage` across for each of them, but
    // for THIS hook it dropped `onStatus`. `setConnectionStatus` then had no
    // call site anywhere, so the state sat at its `useState("connecting")`
    // initial value forever and `isConnected` was a hard `false`.
    //
    // That is not a cosmetic bug. `useGlobalAlerts().isConnected` is what the
    // Markets header renders as LIVE / RECONNECTING and the Floor header as
    // LIVE / STALE, so from 08-14 onward EVERY screen told the reader the feed
    // was down. Measured on production 2026-08-15: one socket, open at +0.1s,
    // delivering messages, badge reading RECONNECTING for the full 30s window.
    // A risk product whose own liveness indicator is stuck on "not live"
    // teaches its readers to distrust the one signal that must never be
    // decorative — and it did it while the data behind it was fine.
    //
    // `subscribeMarketSocket` hands a newcomer the CURRENT status on
    // subscribe (marketSocket.ts:178), so a listener attaching after the
    // socket is already open still resolves correctly rather than waiting for
    // the next transition that may never come.
    return subscribeMarketSocket(getWebSocketUrl(), {
      onStatus: setConnectionStatus,
      onMessage: (message) => {

      if (message.type === "alert" && message.data) {
        const row = message.data;
        const newAlert: Alert = {
          id: String(row.id ?? Date.now()),
          priority: row.priority || "moderate",
          title: row.title || publicMarketLabel(row.alertType, "Unusual market activity"),
          asset: row.asset || row.symbol || "MARKET_WIDE",
          price: row.price == null ? null : Number(row.price),
          priceChange: row.priceChange == null ? null : Number(row.priceChange),
          description: row.description || row.message || "",
          timeToImpact: Number(row.timeToImpact ?? 0),
          confidence: Number(row.confidence ?? 0),
          recommendations: Array.isArray(row.recommendations) ? row.recommendations : [row.description || "Monitor closely"],
          timestamp: row.timestamp ? new Date(row.timestamp) : new Date(),
          engine: row.engine || "statistical",
          isRead: false,
        };

        setWsAlerts((prev) => {
          if (prev.some((a) => a.id === newAlert.id)) return prev;
          return [newAlert, ...prev].slice(0, 100);
        });
        setNewAlertCount((prev) => prev + 1);
      }

      if (
        (message.type === "institutional_signal" || message.type === "smart_money") &&
        message.data
      ) {
        const row = message.data;
        const toast: IntelToast = {
          id: String(row.id ?? Date.now()),
          type: message.type,
          title: row.title || "New market intelligence",
          message: row.message || row.description || "New intelligence received",
          timestamp: row.timestamp ? new Date(row.timestamp) : new Date(),
        };
        setIntelToasts((prev) => {
          if (prev.some((t) => t.id === toast.id)) return prev;
          return [toast, ...prev].slice(0, 10);
        });
      }
      },
    });
  }, [enabled]);

  const clearNewAlerts = useCallback(() => {
    setNewAlertCount(0);
  }, []);

  const clearIntelToasts = useCallback(() => {
    setIntelToasts([]);
  }, []);

  const dismissAlert = useCallback((id: string) => {
    setWsAlerts((prev) => prev.filter((a) => a.id !== id));
    setDismissed((prev) => {
      const next = { ...prev, [id]: Date.now() };
      AsyncStorage.setItem(DISMISSED_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });
  }, []);

  const snoozeAlert = useCallback((id: string, durationMs: number = DEFAULT_SNOOZE_MS) => {
    setSnoozed((prev) => {
      const next = { ...prev, [id]: Date.now() + durationMs };
      AsyncStorage.setItem(SNOOZED_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });
  }, []);

  const markAsRead = useCallback((id: string) => {
    setWsAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, isRead: true } : a))
    );
  }, []);

  return {
    alerts,
    heldAlerts,
    heldSymbolCount: heldQuery.data ? heldQuery.data.heldSymbolCount : null,
    status,
    heldStatus,
    intelToasts,
    isConnected: connectionStatus === "connected",
    connectionStatus,
    newAlertCount,
    clearNewAlerts,
    dismissAlert,
    snoozeAlert,
    markAsRead,
    refetch: () => { refetchRest(); heldQuery.refetch(); },
    clearIntelToasts,
  };
}
